import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Theme } from '@material-ui/core';
import colorPalette from '../themes/colorPalette';
import Typo from './Typo';
import clsx from 'clsx';

const { white, grey, primary } = colorPalette;

type CustomButtonProps = PropTypes.InferProps<typeof propTypes> & {
  primitiveType?: 'button' | 'reset' | 'submit';
  size?: 'small' | 'medium' | 'large';
  type?: 'secondary' | 'primary';
  [key: string]: any;
};

const buttonColors = {
  secondary: {
    backgroundColor: white,
    borderColor: grey[4],
    color: grey[10],
    hover: {
      backgroundColor: white,
    },
  },
  primary: {
    backgroundColor: primary.main,
    borderColor: primary.main,
    color: white,
    hover: {
      backgroundColor: primary.dark,
    },
  },
};

type StylesProps = {
  size: 'small' | 'medium' | 'large';
  type: 'secondary' | 'primary';
  fullWidth?: boolean | null;
};
const useStyles = makeStyles<Theme, StylesProps>({
  btn: ({ size, type, fullWidth }) => ({
    ...getButtonStylesBySize(size),
    border: `1px solid ${buttonColors[type].borderColor}`,
    backgroundColor: buttonColors[type].backgroundColor,
    width: fullWidth ? '100%' : 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: '200ms',
    '&:disabled': {
      opacity: 0.5,
    },
    '&:focus': {
      outline: 'none',
    },
    '&:hover:enabled': {
      backgroundColor: buttonColors[type].hover.backgroundColor,
      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
      cursor: 'pointer',
    },
    '&:active:enabled': {
      backgroundColor: buttonColors[type].hover.backgroundColor,
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
    },
  }),
  text: ({ type }) => ({
    color: buttonColors[type].color,
    margin: '0 4px',
  }),
  startIcon: {
    marginRight: '4px',
  },
  endIcon: {
    marginLeft: '4px',
  },
});

function CustomButton({
  children,
  size = 'medium',
  type = 'secondary',
  startIcon,
  endIcon,
  fullWidth,
  className,
  primitiveType,
  ...other
}: CustomButtonProps) {
  const classes = useStyles({ size, type, fullWidth });
  const allClasses = clsx(classes.btn, className);
  return (
    <button className={allClasses} type={primitiveType ?? 'button'} {...other}>
      {startIcon && startIcon}
      <Typo
        className={classes.text}
        variant={size === 'large' || size === 'medium' ? 'bodyMd' : 'bodySm'}
        fontWeight="medium"
      >
        {children}
      </Typo>
      {endIcon && endIcon}
    </button>
  );
}

function getButtonStylesBySize(size: string) {
  switch (size) {
    case 'small':
      return { padding: '10px 16px', borderRadius: '12px', fontSize: '15px' };
    case 'medium':
      return { padding: '12px 20px', borderRadius: '16px', fontSize: '17px' };
    case 'large':
      return { padding: '16px 24px', borderRadius: '16px', fontSize: '17px' };
    default:
      throw new Error('Invalid size');
  }
}

const propTypes = {
  children: PropTypes.node,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

CustomButton.propTypes = propTypes;

export default CustomButton;
